import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import '../styles/creative-upload.css';
import { extractErrorMessage } from '../utils/errorMessage';
import { useTranslation } from 'react-i18next';

interface Creative {
  id: number;
  advertiser_id: string;
  display_name: string;
  creative_type: 'IMAGE' | 'HTML5' | 'VIDEO';
  ads_creative_id: string | null;
  review_status: string;
  click_url: string | null;
  tracking_urls: string[] | null;  // API returns array
  width: number | null;
  height: number | null;
  has_file: boolean;
  has_thumbnail: boolean;
  preview_url: string | null;
  // Local edit state
  newClickUrl?: string;
  newTrackingCode?: string;
  saveSuccess?: boolean;
}

type EditMode = 'batch' | 'individual';

const EditCreativesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { advertiserId, ids: idsParam } = useParams<{ advertiserId: string; ids: string }>();
  const { user } = useAuth();
  const isAnalyst = user?.role === 'analyst' || user?.role === 'agency_analyst';

  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<EditMode>('batch');
  const [selectedCreativeId, setSelectedCreativeId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveComplete, setSaveComplete] = useState(false);

  // Batch mode values
  const [batchClickUrl, setBatchClickUrl] = useState('');
  const [batchTrackingCode, setBatchTrackingCode] = useState('');

  const ids = idsParam?.split(',').map(Number).filter(Boolean) || [];

  useEffect(() => {
    if (ids.length > 0 && advertiserId) {
      loadCreatives();
    }
  }, [idsParam, advertiserId]);

  const loadCreatives = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/creatives?advertiser_id=${advertiserId}`);
      const allCreatives = response.data.creatives || [];
      // Helper to extract tracking code from array
      const getTrackingCode = (c: any): string => {
        if (c.tracking_urls && Array.isArray(c.tracking_urls) && c.tracking_urls.length > 0) {
          return c.tracking_urls[0];
        }
        return '';
      };

      const selectedCreatives = allCreatives
        .filter((c: Creative) => ids.includes(c.id))
        .map((c: any) => ({
          ...c,
          newClickUrl: c.click_url || '',
          newTrackingCode: getTrackingCode(c),
        }));
      setCreatives(selectedCreatives);

      // Set first as selected
      if (selectedCreatives.length > 0) {
        setSelectedCreativeId(selectedCreatives[0].id);
      }
      // Single file - use individual mode
      if (selectedCreatives.length === 1) {
        setEditMode('individual');
      }

      // Pre-fill batch fields if all creatives share the same values
      if (selectedCreatives.length > 1) {
        const clickUrls = selectedCreatives.map((c: Creative) => c.click_url || '');
        const trackingCodes = selectedCreatives.map((c: any) => c.newTrackingCode || '');

        // If all click URLs are the same and not empty, pre-fill
        if (clickUrls.every((url: string) => url === clickUrls[0]) && clickUrls[0]) {
          setBatchClickUrl(clickUrls[0]);
        }

        // If all tracking codes are the same and not empty, pre-fill
        if (trackingCodes.every((code: string) => code === trackingCodes[0]) && trackingCodes[0]) {
          setBatchTrackingCode(trackingCodes[0]);
        }
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, t('creatives.loadError')));
    } finally {
      setLoading(false);
    }
  };

  const getFileUrl = (creative: Creative): string | null => {
    if (creative.has_file) {
      return `/api/creatives/${creative.id}/file`;
    }
    return creative.preview_url;
  };

  const getHtml5PreviewUrl = (creative: Creative): string => {
    return `/api/creatives/${creative.id}/html5`;
  };

  const getThumbnailUrl = (creative: Creative): string | null => {
    if (creative.creative_type === 'VIDEO' && creative.has_thumbnail) {
      return `/api/creatives/${creative.id}/thumbnail`;
    }
    return null;
  };

  const updateCreativeField = (id: number, field: 'newClickUrl' | 'newTrackingCode', value: string) => {
    setCreatives(prev => prev.map(c =>
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const applyBatchToAll = () => {
    setCreatives(prev => prev.map(c => ({
      ...c,
      newClickUrl: batchClickUrl || c.newClickUrl,
      newTrackingCode: batchTrackingCode || c.newTrackingCode,
    })));
  };

  const saveAllCreatives = async () => {
    setIsSaving(true);
    setSaveComplete(false);

    try {
      const updates = creatives.map(c => ({
        id: c.id,
        click_url: c.newClickUrl,
        tracking_url: c.newTrackingCode,
      }));

      await api.put(`/api/creatives/bulk-update`, { updates });

      setSaveComplete(true);
      setCreatives(prev => prev.map(c => ({ ...c, saveSuccess: true })));

      setTimeout(() => {
        navigate('/creatives');
      }, 1500);
    } catch (err: any) {
      setError(extractErrorMessage(err, t('creatives.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'IMAGE':
        return <svg viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>;
      case 'VIDEO':
        return <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>;
      case 'HTML5':
        return <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>;
      default:
        return null;
    }
  };

  const selectedCreative = creatives.find(c => c.id === selectedCreativeId);
  // All creatives being batch-edited are video → tracking is a plain URL, not an HTML tag.
  const allVideo = creatives.length > 0 && creatives.every(c => c.creative_type === 'VIDEO');
  const overallProgress = isSaving ? 50 : 0;

  if (loading) {
    return (
      <div className="upload-page">
        <header className="upload-header">
          <div className="upload-header-content">
            <div className="upload-header-text">
              <h1>{t('creatives.editTitle')}</h1>
              <p>{t('creatives.loadingShort')}</p>
            </div>
          </div>
        </header>
        <div className="upload-content">
          <div className="loading-state">
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="upload-page">
      {/* Header */}
      <header className="upload-header">
        <div className="upload-header-content">
          <div className="upload-header-text">
            <h1>{t('creatives.editTitleN', { count: creatives.length })}</h1>
            <p>
              {saveComplete
                ? t('creatives.changesSaved')
                : isSaving
                ? t('creatives.saving')
                : t('creatives.updateTracking')
              }
            </p>
          </div>
          <div className="upload-header-actions">
            {isAnalyst ? (
              <button className="btn-secondary" onClick={() => navigate('/creatives')}>
                {t('creatives.back')}
              </button>
            ) : isSaving ? (
              <button className="btn-primary uploading" disabled>
                <div className="upload-btn-progress" style={{ width: `${overallProgress}%` }} />
                <span className="upload-btn-text">
                  <span className="spinner" />
                  {t('creatives.saving')}
                </span>
              </button>
            ) : saveComplete ? (
              <button className="btn-primary" disabled>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
                {t('creatives.saved')}
              </button>
            ) : (
              <>
                <button className="btn-secondary" onClick={() => navigate('/creatives')}>
                  {t('creatives.cancel')}
                </button>
                <button className="btn-primary" onClick={saveAllCreatives}>
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  {t('creatives.saveChanges')}
                </button>
              </>
            )}
          </div>
        </div>
        {isSaving && (
          <div className="upload-progress-bar" style={{ width: `${overallProgress}%` }} />
        )}
      </header>

      <div className="upload-content">
        {/* Left Panel - Preview & File List */}
        <div className="upload-left-panel">
          {/* Preview Zone - shows selected creative */}
          {selectedCreative && (
            <div className="drop-zone has-files edit-mode">
              <div className="drop-zone-border"></div>
              <div className="edit-preview-wrapper">
                {selectedCreative.creative_type === 'HTML5' ? (
                  <iframe
                    src={getHtml5PreviewUrl(selectedCreative)}
                    title={selectedCreative.display_name}
                    className="edit-preview-iframe"
                    style={{
                      width: selectedCreative.width ? `${selectedCreative.width}px` : '100%',
                      height: selectedCreative.height ? `${selectedCreative.height}px` : '250px',
                      maxWidth: '100%',
                    }}
                  />
                ) : selectedCreative.creative_type === 'VIDEO' ? (
                  <video
                    src={getFileUrl(selectedCreative) || ''}
                    controls
                    className="edit-preview-video"
                    poster={getThumbnailUrl(selectedCreative) || undefined}
                  />
                ) : (
                  <img
                    src={getFileUrl(selectedCreative) || ''}
                    alt={selectedCreative.display_name}
                    className="edit-preview-image"
                  />
                )}
              </div>
            </div>
          )}

          {/* File List - same structure as upload page */}
          <div className="file-list">
            <div className="file-list-header">
              <h3>{t('creatives.nSelected', { count: creatives.length })}</h3>
            </div>

            <div className="file-grid">
              {creatives.map((creative, index) => {
                const isSelected = selectedCreativeId === creative.id;
                // For HTML5, don't try to load the zip as an image - use placeholder
                const thumbUrl = creative.creative_type === 'VIDEO'
                  ? getThumbnailUrl(creative)
                  : creative.creative_type === 'HTML5'
                  ? null  // HTML5 uses placeholder
                  : getFileUrl(creative);

                const cardClasses = [
                  'file-card',
                  isSelected && 'selected',
                  isSaving && !creative.saveSuccess && 'uploading',
                  creative.saveSuccess && 'upload-success',
                ].filter(Boolean).join(' ');

                return (
                  <div
                    key={creative.id}
                    className={cardClasses}
                    onClick={() => setSelectedCreativeId(creative.id)}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="file-card-preview">
                      {thumbUrl ? (
                        <img src={thumbUrl} alt={creative.display_name} />
                      ) : (
                        <div className={`file-card-placeholder ${creative.creative_type.toLowerCase()}`}>
                          {getTypeIcon(creative.creative_type)}
                        </div>
                      )}
                      <div className="file-card-type">
                        {getTypeIcon(creative.creative_type)}
                      </div>

                      {/* Saving or Success badge */}
                      {(isSaving || creative.saveSuccess) && (
                        <div className="file-card-badges">
                          {isSaving && !creative.saveSuccess ? (
                            <div className="file-badge pending">
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                              </svg>
                            </div>
                          ) : creative.saveSuccess && (
                            <div className="file-badge success">
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="file-card-info">
                      <span className="file-card-name" title={creative.display_name}>
                        {creative.display_name}
                      </span>
                      <div className="file-card-meta-row">
                        <span className="file-card-dims">
                          {creative.width && creative.height
                            ? `${creative.width}×${creative.height}`
                            : creative.creative_type}
                        </span>
                        <span className="file-card-id">ID: {creative.id}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Panel - Edit Form */}
        <div className="upload-right-panel">
          <div className="settings-card">
            <div className="settings-header">
              <h2>{t('creatives.trackingSettings')}</h2>
              <p>{t('creatives.trackingSubtitle')}</p>
            </div>

            {/* Mode Toggle - only for multiple creatives */}
            {creatives.length > 1 && (
              <div className="mode-toggle">
                <button
                  className={`mode-toggle-btn ${editMode === 'batch' ? 'active' : ''}`}
                  onClick={() => setEditMode('batch')}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"/>
                  </svg>
                  {t('creatives.applyToAll')}
                </button>
                <button
                  className={`mode-toggle-btn ${editMode === 'individual' ? 'active' : ''}`}
                  onClick={() => setEditMode('individual')}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 3h8v8H3zm0 10h8v8H3zm10-10h8v8h-8zm0 10h8v8h-8z"/>
                  </svg>
                  {t('creatives.individual')}
                </button>
                <div className={`mode-toggle-indicator ${editMode}`}></div>
              </div>
            )}

            {/* Batch Mode Form */}
            {editMode === 'batch' && creatives.length > 1 && (
              <div className="settings-form batch-form">
                <div className="form-group">
                  <label>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                    </svg>
                    {t('creatives.clickUrl')}
                  </label>
                  <input
                    type="url"
                    placeholder="https://example.com/landing"
                    value={batchClickUrl}
                    onChange={(e) => setBatchClickUrl(e.target.value)}
                  />
                  <span className="form-hint">{t('creatives.landingHintAll')}</span>
                </div>

                <div className="form-group">
                  <label>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
                    </svg>
                    {t(allVideo ? 'creatives.trackingUrl' : 'creatives.htmlTrackingCode')}
                  </label>
                  <textarea
                    placeholder={allVideo
                      ? 'https://tracker.example.com/impression'
                      : "<img src='https://tracker.example.com/pixel' />"}
                    value={batchTrackingCode}
                    onChange={(e) => setBatchTrackingCode(e.target.value)}
                    rows={allVideo ? 2 : 4}
                  />
                  <span className="form-hint">
                    {t(allVideo ? 'creatives.videoHintAll' : 'creatives.pixelHintAll')}
                  </span>
                </div>

                <button className="btn-apply" onClick={applyBatchToAll}>
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  {t('creatives.applyToAllN', { count: creatives.length })}
                </button>
              </div>
            )}

            {/* Individual Mode Form */}
            {(editMode === 'individual' || creatives.length === 1) && selectedCreative && (
              <div className="settings-form individual-form">
                {/* Selected file header */}
                <div className="selected-file-header">
                  <div className="selected-file-preview">
                    {selectedCreative.creative_type === 'HTML5' ? (
                      // HTML5 always shows placeholder (can't display zip as image)
                      <div className="file-placeholder html5">
                        {getTypeIcon('HTML5')}
                      </div>
                    ) : (selectedCreative.has_file || selectedCreative.preview_url) ? (
                      <img
                        src={
                          selectedCreative.creative_type === 'VIDEO'
                            ? getThumbnailUrl(selectedCreative) || getFileUrl(selectedCreative) || ''
                            : getFileUrl(selectedCreative) || ''
                        }
                        alt=""
                      />
                    ) : (
                      <div className={`file-placeholder ${selectedCreative.creative_type.toLowerCase()}`}>
                        {getTypeIcon(selectedCreative.creative_type)}
                      </div>
                    )}
                  </div>
                  <div className="selected-file-info">
                    <span className="selected-file-name">{selectedCreative.display_name}</span>
                    <span className="selected-file-meta">
                      {selectedCreative.creative_type}
                      {selectedCreative.width && selectedCreative.height &&
                        ` • ${selectedCreative.width}×${selectedCreative.height}`}
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                    </svg>
                    {t('creatives.clickUrl')}
                  </label>
                  <input
                    type="url"
                    placeholder="https://example.com/landing"
                    value={selectedCreative.newClickUrl || ''}
                    onChange={(e) => updateCreativeField(selectedCreative.id, 'newClickUrl', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
                    </svg>
                    {t(selectedCreative.creative_type === 'VIDEO' ? 'creatives.trackingUrl' : 'creatives.htmlTrackingCode')}
                  </label>
                  <textarea
                    placeholder={selectedCreative.creative_type === 'VIDEO'
                      ? 'https://tracker.example.com/impression'
                      : "<img src='https://tracker.example.com/pixel' />"}
                    value={selectedCreative.newTrackingCode || ''}
                    onChange={(e) => updateCreativeField(selectedCreative.id, 'newTrackingCode', e.target.value)}
                    rows={selectedCreative.creative_type === 'VIDEO' ? 2 : 4}
                  />
                  {selectedCreative.creative_type === 'VIDEO' && (
                    <span className="field-hint" style={{ fontSize: 12, color: '#888' }}>
                      {t('creatives.videoImpressionHint')}
                    </span>
                  )}
                </div>

                {/* Navigation - only for multiple in individual mode */}
                {creatives.length > 1 && (
                  <div className="file-navigation">
                    <button
                      className="btn-nav"
                      disabled={creatives.findIndex(c => c.id === selectedCreativeId) === 0}
                      onClick={() => {
                        const idx = creatives.findIndex(c => c.id === selectedCreativeId);
                        if (idx > 0) setSelectedCreativeId(creatives[idx - 1].id);
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                      </svg>
                      {t('creatives.previous')}
                    </button>
                    <span className="file-counter">
                      {creatives.findIndex(c => c.id === selectedCreativeId) + 1} / {creatives.length}
                    </span>
                    <button
                      className="btn-nav"
                      disabled={creatives.findIndex(c => c.id === selectedCreativeId) === creatives.length - 1}
                      onClick={() => {
                        const idx = creatives.findIndex(c => c.id === selectedCreativeId);
                        if (idx < creatives.length - 1) setSelectedCreativeId(creatives[idx + 1].id);
                      }}
                    >
                      {t('creatives.next')}
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCreativesPage;
